import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import api, { downloadCsv } from "@/lib/api";
import { Section, Btn, Input, Textarea, Select, Field, cardCls, StatusPill, EmptyState } from "@/components/admin/ui";
import Modal from "@/components/admin/Modal";
import FileUpload from "@/components/admin/FileUpload";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Active", "In Development", "Future Venture"];
const EMPTY = {
  name: "", tagline: "", status: "In Development", industry: "", website: "", launch_date: "",
  public_summary: "", description: "", logo_url: null, cover_url: null, accent: "#B89C65",
  revenue: 0, costs: 0, profit: 0, marketing: "", notes: "", published: true, order: 0,
};

export default function VenturesAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ceo" || user?.role === "admin";
  const [ventures, setVentures] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/ventures").then(({ data }) => setVentures(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (v) => { setForm({ ...EMPTY, ...v }); setEditing(v.id); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name) { toast.error("Venture name is required"); return; }
    try {
      const payload = { ...form, revenue: Number(form.revenue) || 0, costs: Number(form.costs) || 0, profit: Number(form.profit) || 0 };
      if (editing === "new") await api.post("/admin/ventures", payload);
      else await api.patch(`/admin/ventures/${editing}`, payload);
      toast.success("Venture saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const togglePublish = async (v) => {
    await api.patch(`/admin/ventures/${v.id}`, { published: !v.published });
    load();
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/ventures/${v.id}`);
    toast.success("Venture deleted");
    load();
  };

  return (
    <div data-testid="admin-ventures">
      <Section
        title="Ventures"
        subtitle="Manage every business in the Black Diamond ecosystem. Add new ventures without developer help."
        action={<div className="flex gap-3">
          <Btn variant="outline" data-testid="venture-export" onClick={() => downloadCsv("ventures", "ventures.csv")}><Download size={15} /> Export CSV</Btn>
          {isAdmin && <Btn data-testid="venture-add" onClick={openNew}><Plus size={15} /> Add Venture</Btn>}
        </div>}
      >
        {ventures.length === 0 ? (
          <EmptyState>No ventures yet.</EmptyState>
        ) : (
          <div className={`${cardCls} divide-y divide-white/5`}>
            {ventures.map((v) => (
              <div key={v.id} data-testid={`venture-row-${v.slug}`} className="flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-bd-black border border-white/10" style={{ color: v.accent }}>
                  {v.logo_url ? <img src={v.logo_url} alt="" className="w-full h-full object-contain" /> : <span className="font-serif text-lg">{v.name[0]}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-bd-ivory truncate">{v.name}</span>
                    <StatusPill status={v.status} />
                    {!v.published && <span className="text-[0.6rem] uppercase tracking-wider text-bd-ivory/30">Hidden</span>}
                  </div>
                  <p className="text-xs text-bd-ivory/40 truncate mt-0.5">{v.industry}</p>
                </div>
                <div className="flex items-center gap-1">
                  {isAdmin && (
                    <button data-testid={`venture-toggle-${v.slug}`} onClick={() => togglePublish(v)} title={v.published ? "Hide" : "Publish"} className="p-2 text-bd-ivory/40 hover:text-bd-gold transition-colors">
                      {v.published ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  )}
                  <a href={`/ventures/${v.slug}`} target="_blank" rel="noreferrer" className="p-2 text-bd-ivory/40 hover:text-bd-gold transition-colors"><ExternalLink size={16} /></a>
                  <button data-testid={`venture-edit-${v.slug}`} onClick={() => openEdit(v)} className="p-2 text-bd-ivory/40 hover:text-bd-gold transition-colors"><Pencil size={16} /></button>
                  {isAdmin && <button data-testid={`venture-delete-${v.slug}`} onClick={() => remove(v)} className="p-2 text-bd-ivory/40 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Add Venture" : "Edit Venture"} wide testId="venture-modal">
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Venture name"><Input data-testid="venture-name" value={form.name} onChange={set("name")} placeholder="e.g. Black Diamond Florist" /></Field>
            <Field label="Status"><Select data-testid="venture-status" value={form.status} onChange={set("status")}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</Select></Field>
            <Field label="Industry"><Input value={form.industry} onChange={set("industry")} placeholder="e.g. Retail" /></Field>
            <Field label="Website"><Input value={form.website || ""} onChange={set("website")} placeholder="https://" /></Field>
            <Field label="Launch date"><Input value={form.launch_date || ""} onChange={set("launch_date")} placeholder="e.g. 2026" /></Field>
            <Field label="Accent colour"><Input type="color" value={form.accent || "#B89C65"} onChange={set("accent")} className="h-10 p-1" /></Field>
          </div>
          <Field label="Tagline"><Input value={form.tagline || ""} onChange={set("tagline")} placeholder="One line summary" /></Field>
          <Field label="Public summary (shown on website)"><Textarea rows={4} value={form.public_summary || ""} onChange={set("public_summary")} placeholder="Credible, interesting — protect confidential plans." /></Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Logo"><FileUpload value={form.logo_url} onChange={(url) => setForm((f) => ({ ...f, logo_url: url }))} label="Upload logo" /></Field>
            <Field label="Cover image"><FileUpload value={form.cover_url} onChange={(url) => setForm((f) => ({ ...f, cover_url: url }))} label="Upload cover" /></Field>
          </div>

          <div className="border-t border-white/10 pt-5">
            <p className="overline text-bd-gold mb-1">Private management (not shown publicly)</p>
            <p className="text-xs text-bd-ivory/35 mb-4">Demo financials — for planning only.</p>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Revenue (£)"><Input type="number" value={form.revenue} onChange={set("revenue")} /></Field>
              <Field label="Costs (£)"><Input type="number" value={form.costs} onChange={set("costs")} /></Field>
              <Field label="Profit (£)"><Input type="number" value={form.profit} onChange={set("profit")} /></Field>
            </div>
            <div className="mt-4">
              <Field label="Internal notes"><Textarea rows={3} value={form.notes || ""} onChange={set("notes")} /></Field>
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-bd-ivory/70">
            <input type="checkbox" data-testid="venture-published" checked={!!form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="accent-bd-gold w-4 h-4" />
            Published on public website
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn data-testid="venture-save" onClick={save}>Save Venture</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
